import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPerson, getPerson, listPeople, updatePerson } from '@/api/people';
import { approveElmoStarter, getElmoStarter } from '@/api/elmoOnboarding';
import { getLocations } from '@/api/locations';
import { getCodeBoard } from '@/api/baseplanCodes';
import { listPositionTitles } from '@/api/positionTitles';
import { listDepartments } from '@/api/departments';
import { ApiError } from '@/api/client';
import { personSchema, accountStatuses, homeBranchGroups, type PersonFormData } from './schema';
import type { PersonWrite, PositionTitle } from '@/types/oscar';

// Fixed domain list for the Username → Primary email composer. onsite.com.au is
// the default. The composed email stays editable (guests / externals differ).
const EMAIL_DOMAINS = ['onsite.com.au', 'sime.com'] as const;

const emptyDefaults: PersonFormData = {
  EmployeeId: '',
  DisplayName: '',
  PreferredName: '',
  FirstName: '',
  LastName: '',
  PrimaryEmail: '',
  LocationId: null,
  CostCentreCode: '',
  JobTitle: '',
  Department: '',
  DepartmentCode: null,
  ManagerPersonId: null,
  Mobile: '',
  Phone: '',
  Username: '',
  EmploymentStartDate: '',
  HomeBranchCode: '',
  AccountStatus: 'Active',
  Notes: '',
};

/** Strip empty strings → undefined so the API maps them to NULL. */
function toPayload(data: PersonFormData): PersonWrite {
  const blankToUndef = (v?: string) => (v && v.trim() !== '' ? v.trim() : undefined);
  return {
    EmployeeId: blankToUndef(data.EmployeeId),
    DisplayName: data.DisplayName.trim(),
    PreferredName: blankToUndef(data.PreferredName),
    FirstName: data.FirstName.trim(),
    LastName: data.LastName.trim(),
    PrimaryEmail: data.PrimaryEmail.trim(),
    LocationId: data.LocationId ?? undefined,
    CostCentreCode: blankToUndef(data.CostCentreCode),
    JobTitle: blankToUndef(data.JobTitle),
    Department: blankToUndef(data.Department),
    // Empty string clears the department link server-side; a code sets it.
    DepartmentCode: data.DepartmentCode ?? '',
    ManagerPersonId: data.ManagerPersonId ?? undefined,
    Mobile: blankToUndef(data.Mobile),
    Phone: blankToUndef(data.Phone),
    Username: blankToUndef(data.Username),
    EmploymentStartDate: blankToUndef(data.EmploymentStartDate),
    // The termination date is NOT sent: it is set on the Leaving tab, and an absent
    // date means "leave alone" server-side. Empty string clears the primary branch.
    HomeBranchCode: data.HomeBranchCode?.trim() ?? '',
    AccountStatus: data.AccountStatus,
    Notes: blankToUndef(data.Notes),
  };
}

export function PersonEditForm() {
  const { id, elmoUserId } = useParams();
  const personId = id ? Number(id) : null;
  const isEdit = personId !== null;
  // PR-36: the same form, reached from Admin > Onboarding > New starters, creates
  // the person by APPROVING an ELMO onboarding record instead of a bare create.
  // Reusing this form rather than duplicating it means a field added here reaches
  // both paths — and the approver gets the position-title → department-code
  // derivation, manager picker and location list they already know.
  const isElmoApproval = Boolean(elmoUserId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Domain for the Username → Primary email composer (UI-only; not persisted).
  const [domain, setDomain] = useState<string>(EMAIL_DOMAINS[0]);

  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), retry: false });
  const codesQuery = useQuery({ queryKey: ['locations', 'codes'], queryFn: ({ signal }) => getCodeBoard(signal), retry: false });
  const personQuery = useQuery({
    queryKey: ['person', personId],
    queryFn: ({ signal }) => getPerson(personId!, signal),
    enabled: isEdit,
  });
  // The ELMO record being approved, plus the server-built prefill.
  const elmoQuery = useQuery({
    queryKey: ['elmo-starter', elmoUserId],
    queryFn: ({ signal }) => getElmoStarter(elmoUserId!, signal),
    enabled: isElmoApproval,
    retry: false,
  });

  const form = useForm<PersonFormData>({
    resolver: zodResolver(personSchema),
    defaultValues: emptyDefaults,
  });
  const { register, handleSubmit, reset, setValue, watch, formState } = form;

  // Populate the form once the person loads (edit mode).
  useEffect(() => {
    if (personQuery.data) {
      const p = personQuery.data.Person;
      reset({
        EmployeeId: p.EmployeeId ?? '',
        DisplayName: p.DisplayName,
        PreferredName: p.PreferredName ?? '',
        FirstName: p.FirstName,
        LastName: p.LastName,
        PrimaryEmail: p.PrimaryEmail,
        LocationId: p.LocationId,
        CostCentreCode: p.CostCentreCode ?? '',
        JobTitle: p.JobTitle ?? '',
        Department: p.Department ?? '',
        DepartmentCode: p.DepartmentCode ?? null,
        ManagerPersonId: p.ManagerPersonId,
        Mobile: p.Mobile ?? '',
        Phone: p.Phone ?? '',
        Username: p.Username ?? (p.PrimaryEmail.includes('@') ? p.PrimaryEmail.split('@')[0] : ''),
        EmploymentStartDate: p.EmploymentStartDate ? p.EmploymentStartDate.slice(0, 10) : '',
        HomeBranchCode: p.HomeBranchCode ?? '',
        AccountStatus: p.AccountStatus,
        Notes: p.Notes ?? '',
      });
      // Pre-select the domain dropdown from the loaded email (fall back to default).
      const emailDomain = p.PrimaryEmail.includes('@') ? p.PrimaryEmail.split('@').pop()! : '';
      setDomain((EMAIL_DOMAINS as readonly string[]).includes(emailDomain) ? emailDomain : EMAIL_DOMAINS[0]);
    }
  }, [personQuery.data, reset]);

  // Seed the form from ELMO (approval mode). Identity only — job title,
  // department, location, cost centre and manager stay blank because ELMO holds
  // those as IRI references rather than names, so there is nothing honest to
  // pre-fill them with (PR-36 §3). EmployeeId is deliberately left empty too:
  // ELMO's number is on a different scheme and the Oscar column is UNIQUE.
  useEffect(() => {
    const pre = elmoQuery.data?.Prefill;
    if (!pre) return;
    reset({
      ...emptyDefaults,
      DisplayName: pre.DisplayName ?? '',
      PreferredName: pre.PreferredName ?? '',
      FirstName: pre.FirstName ?? '',
      LastName: pre.LastName ?? '',
      PrimaryEmail: pre.PrimaryEmail ?? '',
      Mobile: pre.Mobile ?? '',
      Username: pre.Username ?? '',
      EmploymentStartDate: pre.EmploymentStartDate ? pre.EmploymentStartDate.slice(0, 10) : '',
      AccountStatus: 'Onboarding',
    });
    const emailDomain = pre.PrimaryEmail?.includes('@') ? pre.PrimaryEmail.split('@').pop()! : '';
    setDomain((EMAIL_DOMAINS as readonly string[]).includes(emailDomain) ? emailDomain : EMAIL_DOMAINS[0]);
  }, [elmoQuery.data, reset]);

  const mutation = useMutation({
    mutationFn: async (data: PersonFormData): Promise<{ PersonId: number }> => {
      if (isEdit) return updatePerson(personId!, toPayload(data));
      // Approving records the decision AND creates the person in one server-side
      // transaction, so it must not be a create followed by a second call — a
      // failure between the two would leave a person nobody can trace to a starter.
      if (isElmoApproval) return approveElmoStarter(elmoUserId!, toPayload(data));
      return createPerson(toPayload(data));
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['person', saved.PersonId] });
      if (isElmoApproval) queryClient.invalidateQueries({ queryKey: ['elmo-starters'] });
      navigate(`/people/${saved.PersonId}`);
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not save the person.');
    },
  });

  const onSubmit = (data: PersonFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const titlesQuery = useQuery({
    queryKey: ['position-titles', 'active'],
    queryFn: ({ signal }) => listPositionTitles(undefined, signal),
    retry: false,
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
    retry: false,
  });

  const locations = locationsQuery.data ?? [];
  // Oscar's locations grouped by region (a long flat list was hard to pick from).
  const locationGroups = [...new Set(locations.map((l) => l.RegionDesc ?? 'No region'))]
    .sort((a, b) => (a === 'No region' ? 1 : 0) - (b === 'No region' ? 1 : 0) || a.localeCompare(b))
    .map((label) => ({ label, locations: locations.filter((l) => (l.RegionDesc ?? 'No region') === label) }));
  // What Entra says for an existing person (the account sync's read-only copy).
  const entra = personQuery.data?.Person;
  const selectedLocationId = watch('LocationId');
  const managerId = watch('ManagerPersonId');
  const jobTitle = watch('JobTitle');
  const departmentCode = watch('DepartmentCode');
  const username = watch('Username');
  const homeBranch = watch('HomeBranchCode') ?? '';
  // The selected location's branches are the suggested primary branch (a place can run
  // several), its own primary first. Only active codes on the board (the server refuses any other).
  const selectedLocation = locations.find((l) => l.LocationId === selectedLocationId);
  const locationBranches = selectedLocation?.BranchCodes?.length
    ? selectedLocation.BranchCodes
    : selectedLocation?.BranchCode ? [selectedLocation.BranchCode] : [];
  const locationCodes = locationBranches.filter((code) => (codesQuery.data?.Codes ?? []).some((c) => c.IsActive && c.Code === code));
  const locationCode = locationCodes[0] ?? null;

  // Compose Primary email from username@domain (leaves it editable — guests /
  // externals may not follow the pattern). Only fires on user interaction.
  const compose = (user: string, dom: string) => {
    const u = user.trim();
    if (u) setValue('PrimaryEmail', `${u}@${dom}`, { shouldDirty: true, shouldValidate: true });
  };

  const deptName = new Map((departmentsQuery.data ?? []).map((d) => [d.DepartmentCode, d.DepartmentDescription] as const));
  const derivedDepartment = departmentCode
    ? `${departmentCode}${deptName.get(departmentCode) ? ` — ${deptName.get(departmentCode)}` : ''}`
    : null;

  // Selecting a job title carries its department through — Department itself is
  // never entered by hand (it is derived from the Position Title).
  const selectTitle = (t: PositionTitle | null) => {
    setValue('JobTitle', t?.PositionTitle ?? '', { shouldDirty: true });
    setValue('DepartmentCode', t?.DepartmentCode ?? null, { shouldDirty: true });
  };

  return (
    <section className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-navy">
        {isEdit ? 'Edit person' : isElmoApproval ? 'Approve new starter' : 'Add person'}
      </h1>

      {isElmoApproval && (
        <div className="mt-3 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/90">
          {elmoQuery.isError ? (
            <span className="text-red">Could not load the ELMO record. Go back and try again.</span>
          ) : (
            <>
              <p>
                Creating an Oscar person from the ELMO onboarding record for{' '}
                <strong>{elmoQuery.data?.Starter.FullName ?? elmoQuery.data?.Starter.Email ?? elmoUserId}</strong>
                {elmoQuery.data?.Starter.ElmoEmployeeNumber && (
                  <> (ELMO staff no. {elmoQuery.data.Starter.ElmoEmployeeNumber} &mdash; not copied; Oscar issues its own)</>
                )}.
              </p>
              <p className="mt-1">
                Name, email, mobile and start date came from ELMO. <strong>Position title, location,
                cost centre and manager are yours to set</strong> &mdash; ELMO holds those as internal
                references rather than names, so there is nothing to copy across.
              </p>
              <p className="mt-1 text-orange">
                Choosing a position title sets the department code, and saving then allocates that
                department&rsquo;s mandatory role software to this person.
              </p>
            </>
          )}
        </div>
      )}

      {submitError && (
        <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Display name" error={formState.errors.DisplayName?.message}>
            <input {...register('DisplayName')} className={inputCls} />
          </Field>
          <Field label="Preferred name">
            <input {...register('PreferredName')} className={inputCls} />
          </Field>
          <Field label="First name" error={formState.errors.FirstName?.message}>
            <input {...register('FirstName')} className={inputCls} />
          </Field>
          <Field label="Last name" error={formState.errors.LastName?.message}>
            <input {...register('LastName')} className={inputCls} />
          </Field>
          <Field label="Username">
            <input
              {...register('Username', { onChange: (e) => compose(e.target.value, domain) })}
              className={inputCls}
              placeholder="e.g. anthony.bolack"
            />
            {/* Say why the box is empty. ELMO holds whatever address the starter signed
                up with — often a personal one — and the corporate address is Oscar's to
                determine, so it is deliberately not carried across. Without this note an
                empty field reads as a bug and invites someone to copy it over by hand. */}
            {elmoQuery.data?.Prefill?.ElmoEmailWithheld && (
              <p className="mt-1 text-xs text-slate-600">
                ELMO holds a personal email address for this starter, so it has not been
                carried across. Enter the Onsite username — the address is composed below.
              </p>
            )}
          </Field>
          <Field label="Domain">
            <select
              aria-label="Email domain"
              value={domain}
              onChange={(e) => { setDomain(e.target.value); compose(username ?? '', e.target.value); }}
              className={inputCls}
            >
              {EMAIL_DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Primary email (composed — editable)" error={formState.errors.PrimaryEmail?.message}>
            <input {...register('PrimaryEmail')} className={inputCls} />
          </Field>
          <Field label="Employee ID">
            <input {...register('EmployeeId')} className={inputCls} />
          </Field>
          <Field label="Mobile">
            <input {...register('Mobile')} className={inputCls} />
          </Field>
          <Field label="Work phone" error={formState.errors.Phone?.message}>
            <input {...register('Phone')} className={inputCls} />
          </Field>
          <div>
            <Field label="Location">
              <select {...register('LocationId', { setValueAs: (v) => (v === '' || v == null ? null : Number(v)) })} className={inputCls}>
                <option value="">—</option>
                {locationGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.locations.map((l) => (
                      <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}{l.BranchCode ? ` (${l.BranchCode})` : ''}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            {entra?.EntraOfficeLocation && (
              <p className="mt-1 text-xs text-slate-600">
                Entra office: {entra.EntraOfficeLocation}
                {entra.EntraLocationId != null && entra.EntraLocationId !== selectedLocationId && (
                  <button
                    type="button"
                    onClick={() => setValue('LocationId', entra.EntraLocationId ?? null, { shouldDirty: true })}
                    className="ml-2 font-semibold text-navy underline"
                  >
                    Use {entra.EntraLocationName}
                  </button>
                )}
                {entra.EntraLocationId == null && ' - no Oscar location has this AD office yet'}
              </p>
            )}
          </div>
          <div>
            <Field label="Primary branch / cost centre" error={formState.errors.HomeBranchCode?.message}>
              <select {...register('HomeBranchCode')} className={inputCls}>
                <option value="">—</option>
                {homeBranchGroups(codesQuery.data, homeBranch, locationCodes).map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.codes.map((c) => (
                      <option key={c.Code} value={c.Code}>{c.Code} {c.Description}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            <p className="mt-1 text-xs text-slate-600">
              The cost centre (unless a split is set on the profile) and OnsiteLive&apos;s branch - copied there within 15 minutes.
              {locationCode && !locationCodes.includes(homeBranch) && (
                <button
                  type="button"
                  onClick={() => setValue('HomeBranchCode', locationCode, { shouldDirty: true })}
                  className="ml-2 font-semibold text-navy underline"
                >
                  Use {locationCode} (the location&apos;s primary branch)
                </button>
              )}
            </p>
          </div>
          {/* The cost centre is the primary branch's unless a % split is set on the profile
              (Data-Model §4.5.5; team walk-through 26/10/2026) - no free-text code here. */}
          <div className="block text-xs font-semibold text-darkGrey">
            Cost centre
            <p className={`${inputCls} bg-bg font-normal text-darkGrey/80`} aria-label="Cost centre">
              {isEdit && watch('CostCentreCode') ? `${watch('CostCentreCode')} (split on the profile)` : 'The primary branch'}
            </p>
            <span className="mt-1 block font-normal text-slate-600">Split it by percentage on the person&apos;s profile.</span>
          </div>
          <Field label="Job title">
            <PositionTitleCombobox
              value={jobTitle || null}
              titles={titlesQuery.data ?? []}
              deptName={deptName}
              onChange={selectTitle}
            />
          </Field>
          <Field label="Department (derived from job title)">
            <div className={`${inputCls} bg-bg text-darkGrey/80`} aria-label="Department" data-testid="derived-department">
              {derivedDepartment ?? '—'}
            </div>
          </Field>
          <Field label="Employment start date">
            <input type="date" {...register('EmploymentStartDate')} className={inputCls} />
          </Field>
          <Field label="Account status">
            <select {...register('AccountStatus')} className={inputCls}>
              {accountStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <div>
            <Field label="Manager">
              <ManagerPicker
                value={managerId ?? null}
                initialName={personQuery.data?.Person.ManagerDisplayName ?? null}
                onChange={(v) => setValue('ManagerPersonId', v, { shouldDirty: true })}
              />
            </Field>
            {/* Anthony, 26/10/2026: Entra wins - the account sync (every 6 hours) replaces it. */}
            <p className="mt-1 text-xs text-slate-600">
              Follows the manager in AD / Entra: the account sync replaces it every 6 hours. To change it for good, change it in AD.
            </p>
          </div>
        </div>

        <Field label="Notes">
          <textarea {...register('Notes')} rows={3} className={inputCls} />
        </Field>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create person'}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/people/${personId}` : '/people')}
            className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg"
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';


function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-darkGrey">
      {label}
      {children}
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </label>
  );
}

/** Lightweight async manager search — sets ManagerPersonId from a People search.
 *  Displays the resolved manager name (from the loaded person, or the pick),
 *  never a bare "#id". */
function ManagerPicker({
  value,
  initialName,
  onChange,
}: {
  value: number | null;
  initialName: string | null;
  onChange: (v: number | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  // Name of the currently-selected manager. Seeded from the loaded person and
  // updated when a new manager is picked so the chip shows a real name.
  const [name, setName] = useState<string | null>(initialName);
  useEffect(() => { setName(initialName); }, [initialName]);

  const results = useQuery({
    queryKey: ['people-search', term],
    queryFn: ({ signal }) => listPeople({ search: term, pageSize: 8 }, signal),
    enabled: open && term.trim().length >= 2,
  });

  if (value) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <span className="rounded bg-bg px-2 py-1 text-sm font-normal text-navy">{name ?? `Manager #${value}`}</span>
        <button type="button" onClick={() => { onChange(null); setName(null); }} className="text-xs font-normal text-red hover:underline">
          Clear
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        placeholder="Search to set a manager"
        aria-label="Manager search"
        className={inputCls}
      />
      {open && results.data && results.data.Items.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded border border-border bg-white text-sm shadow">
          {results.data.Items.map((p) => (
            <li key={p.PersonId}>
              <button
                type="button"
                onClick={() => { onChange(p.PersonId); setName(p.DisplayName); setOpen(false); setTerm(''); }}
                className="block w-full px-3 py-1.5 text-left font-normal hover:bg-bg"
              >
                {p.DisplayName} <span className="text-darkGrey/60">({p.PrimaryEmail})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Searchable Job Title picker sourced from the Onboarding Position Title list.
 *  Selecting a title carries its department code back to the form (the
 *  Department field is derived, never typed). Shows the current value even if it
 *  predates the Position Title list; clearing resets both title and department. */
function PositionTitleCombobox({
  value,
  titles,
  deptName,
  onChange,
}: {
  value: string | null;
  titles: PositionTitle[];
  deptName: Map<string, string>;
  onChange: (t: PositionTitle | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  const filtered = (() => {
    const q = term.trim().toLowerCase();
    const list = q ? titles.filter((t) => t.PositionTitle.toLowerCase().includes(q)) : titles;
    return list.slice(0, 50);
  })();

  return (
    <div className="relative">
      <input
        role="combobox"
        aria-expanded={open}
        aria-label="Job title"
        value={open ? term : (value ?? '')}
        placeholder="Search job titles"
        onFocus={() => { setOpen(true); setTerm(''); }}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {value && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(null); setTerm(''); setOpen(false); }}
          aria-label="Clear job title"
          className="absolute right-2 top-2.5 text-xs font-normal text-red hover:underline"
        >
          Clear
        </button>
      )}
      {open && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded border border-border bg-white text-sm shadow">
          {filtered.map((t) => (
            <li key={t.PositionTitleId}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(t); setOpen(false); setTerm(''); }}
                className="block w-full px-3 py-1.5 text-left font-normal hover:bg-bg"
              >
                {t.PositionTitle}
                {t.DepartmentCode && (
                  <span className="text-darkGrey/60"> — {t.DepartmentCode}{deptName.get(t.DepartmentCode) ? ` (${deptName.get(t.DepartmentCode)})` : ''}</span>
                )}
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="px-3 py-1.5 text-darkGrey/50">No job titles match.</li>}
        </ul>
      )}
    </div>
  );
}
